// ============================================================================
// retraitRoleAdmin.js : une fiche supprimée ou inactive perd son rôle
// d'administrateur, dans la MÊME écriture (demande de Pierre du 01/10/2026,
// 22 h : « L'effacement de fiche doit entraîner la perte de qualité
// d'administrateur. »).
//
// Constat du 01/10 : la fiche SUPPRIMÉE de Stella ACQUAVIVA (supprime:true,
// actif:false) portait encore role « admin » (corrigé à la main, auditLog
// « retrait_role_admin »). Le serveur et les règles ne la tenaient déjà plus
// pour administratrice (validateCaller refuse une fiche inactive ou
// supprimée ; ficheAdministrateurActive dans firestore.rules lit l'état), mais
// le rôle dormait sur la fiche : une réactivation (Réactiver, Restaurer depuis
// le journal, restauration d'une mutuelle archivée, demande de compte validée)
// le rendait sans que personne ne l'ait décidé, et l'inventaire d'autorité
// comptait une administratrice de plus.
//
// Pur et testé (src/retraitRoleAdmin.test.js). Utilisé par saveDelegate et
// deleteDelegate (src/useFirebaseState.js), seuls écrivains de delegates/ au
// navigateur, et par l'archivage d'une mutuelle (SuperAdminPanel). Miroir
// serveur : functions/lib/administrateurs.js (fusion de fiches, demande de
// compte validée).
//
// Le super-administrateur EN EXERCICE ne se supprime ni ne se désactive : la
// règle le refuse déjà (ficheSuperAdminPreservee) ; on le dit ici en clair,
// avant toute écriture. Le navigateur ne lit pas superadmin/unique (fermée) :
// il reconnaît cette fiche à son rôle « superadmin » (seul
// scripts/set_superadmin.js l'attribue, en SDK admin) sur une fiche humaine
// (pas `technique`), ou à la session super-administratrice sur SA fiche.
// ============================================================================

/** Rôles de fiche qui ouvrent les fonctions d'administration (validateCaller). */
export const ROLES_ADMINISTRATION = Object.freeze(["admin", "superadmin"]);

/** Action du journal : JAMAIS « edit ». Le « Revenir à l'état d'avant » du
 *  journal des accès réécrirait la fiche depuis un avant réduit au rôle. */
export const ACTION_RETRAIT_ROLE_ADMIN = "retrait_role_admin";

export const MESSAGE_REFUS_SUPERADMIN = "La fiche du super-administrateur en exercice ne peut être ni supprimée ni désactivée : "
  + "elle porte l'accès de secours d'Aramis. Pour confier ce rôle à quelqu'un d'autre, l'équipe technique passe "
  + "d'abord par scripts/set_superadmin.js ; la fiche se retire ensuite comme une autre.";

/** Fiche supprimée ou désactivée (les deux drapeaux se valent pour l'accès). */
export const ficheRetiree = (f) => !!f && (f.supprime === true || f.actif === false);

/** Fiche présente, ni supprimée ni désactivée (lib/administrateurs.js). */
export const ficheActive = (f) => !!f && f.actif !== false && f.supprime !== true;

/** Administrateur (admin ou superadmin) sur une fiche ACTIVE : même prédicat,
 *  même nom que lib/administrateurs.js, dont Hestia (src/hestia/mutuelles.js)
 *  reçoit la copie serveur par scripts/sync-hestia-serveur.js. */
export const estFicheAdministrateur = (f) => ficheActive(f) && ROLES_ADMINISTRATION.includes(f.role);

/** Super-administrateur par le rôle de sa fiche, ACTIVE (lib/administrateurs.js). */
export const estFicheSuperAdministrateur = (f) => ficheActive(f) && f.role === "superadmin";

/** La fiche porte un rôle ou un drapeau d'administration. */
export const porteRoleAdmin = (f) => !!f && (ROLES_ADMINISTRATION.includes(f.role) || f.isSuperAdmin === true);

/** La fiche du super-administrateur en exercice (voir l'en-tête). */
export function estFicheSuperAdminEnExercice(f, { id = null, session = null } = {}) {
  if (!f) return false;
  if (f.role === "superadmin" && f.technique !== true) return true;
  const ficheId = id || f.id || null;
  return !!(session && session.role === "superadmin" && ficheId && session.id === ficheId);
}

// ── Le rôle d'administrateur : super-administrateur seul (12/10/2026) ──────
// Décision de Pierre du 12/10/2026 : DONNER ou RETIRER la qualité
// d'administrateur n'est permis qu'au super-administrateur, à la création
// comme à la modification. Miroir EXACT de qualiteAdminInchangee
// (firestore.rules) et de fusionnerDelegates (functions/index.js) : aucun rôle
// « admin » NOUVEAU (même sur une fiche désactivée), aucun « superadmin »
// retiré, et la qualité EFFECTIVE (estFicheAdministrateur : rôle sur fiche
// active) inchangée. Rétrograder, désactiver ou supprimer un administrateur,
// réactiver une fiche qui porte encore le rôle : super-administrateur seul.
// Retirer le rôle qui DORT sur une fiche déjà supprimée reste à tout
// administrateur (il ne donne plus rien).
export const MESSAGE_REFUS_QUALITE_ADMIN = "Donner ou retirer le rôle d'administrateur est réservé au super-administrateur : "
  + "c'est aussi le cas pour désactiver, supprimer ou réactiver la fiche d'un administrateur.";

/** L'écriture (`avant` : la fiche en base, null pour une création ; `apres` :
 *  l'état écrit) donne-t-elle ou retire-t-elle la qualité d'administrateur ? */
export function changeQualiteAdmin(avant, apres) {
  const ra = (avant && avant.role) ?? null;
  const rb = (apres && apres.role) ?? null;
  const roleAdmis = ra === rb || (!ROLES_ADMINISTRATION.includes(rb) && ra !== "superadmin");
  return !(roleAdmis && estFicheAdministrateur(avant) === estFicheAdministrateur(apres));
}

/** Session du super-administrateur (rôle de session émis par le serveur au
 *  login, withRoleCanonique ; la règle relit superadmin/unique). */
export const sessionSuperAdmin = (session) => !!(session && session.role === "superadmin");

/** L'identité d'un administrateur en exercice (e-mail, alias : les clés par
 *  lesquelles le mot de passe et le SSO retrouvent sa fiche) ne change que
 *  par le super-administrateur, sa propre fiche comprise (relecture adverse
 *  du 12/10/2026 ; firestore.rules, gesteAdministrateurPermis). */
export const MESSAGE_REFUS_IDENTITE_ADMIN = "L'adresse e-mail et les alias d'un administrateur servent à se connecter : seul le super-administrateur les change.";
export function refusIdentiteAdmin() {
  const e = new Error(MESSAGE_REFUS_IDENTITE_ADMIN);
  e.code = "failed-precondition";
  e.raison = "identite_admin";
  return e;
}

/** Refus clair, AVANT toute écriture (l'écran affiche le message). */
export function refusQualiteAdmin() {
  const e = new Error(MESSAGE_REFUS_QUALITE_ADMIN);
  e.code = "failed-precondition";
  e.raison = "qualite_admin";
  return e;
}

/** Refus clair, reconnaissable par son code (l'écran affiche le message). */
export function refusSuperAdmin() {
  const e = new Error(MESSAGE_REFUS_SUPERADMIN);
  e.code = "failed-precondition";
  return e;
}

/** Plan d'une écriture de fiche (`apres`, l'état écrit ; `avant`, l'état en
 *  base) par une session d'administration :
 *    • la fiche reste active : rien ;
 *    • la fiche du super-administrateur en exercice sortirait active : refus ;
 *    • sinon, une fiche retirée qui porte un rôle d'administration le perd :
 *      `champs` est à fusionner dans l'écriture ({ role: null }, et
 *      `isSuperAdmin: false` s'il était posé), `retrait` décrit le geste pour
 *      le journal.
 *  Rend { refus } ou { champs, retrait } (retrait null : rien à retirer). */
export function planRetraitRoleAdmin({ avant = null, apres, id = null, session = null }) {
  if (!ficheRetiree(apres)) return { champs: {}, retrait: null };
  const ficheId = id || (apres && apres.id) || (avant && avant.id) || null;
  if (estFicheSuperAdminEnExercice(avant, { id: ficheId, session })
      || estFicheSuperAdminEnExercice(apres, { id: ficheId, session })) {
    return { refus: refusSuperAdmin() };
  }
  // C'est l'état ÉCRIT qui compte : une réécriture qui pose déjà un autre
  // rôle (ou aucun) ne retire rien de plus. Une écriture fusionnée
  // (deleteDelegate) passe la fiche en base complétée de son nouveau statut.
  if (!porteRoleAdmin(apres)) return { champs: {}, retrait: null };
  const champs = { role: null };
  if (apres.isSuperAdmin === true) champs.isSuperAdmin = false;
  return {
    champs,
    retrait: {
      roleAvant: ROLES_ADMINISTRATION.includes(apres.role) ? apres.role : null,
      isSuperAdminAvant: apres.isSuperAdmin === true,
      motif: apres.supprime === true ? "suppression" : "inactivation",
    },
  };
}

/** Entrée du journal (auditLog) du retrait, sans aucune autre donnée de la
 *  fiche que son nom : avant et après ne portent que le rôle. */
export function entreeJournalRetrait({ id, fiche, retrait, acteur = "Admin", ts = Date.now(), entreeId }) {
  const f = fiche || {};
  const libelle = `${f.prenom || ""} ${f.nom || ""}`.trim() || id;
  const avant = { role: retrait.roleAvant };
  if (retrait.isSuperAdminAvant) avant.isSuperAdmin = true;
  return {
    id: entreeId, ts, action: ACTION_RETRAIT_ROLE_ADMIN,
    collection: "delegates", entityId: id, entityLabel: libelle,
    before: avant, after: { role: null },
    motif: retrait.motif,
    details: `${retrait.roleAvant ? `Rôle « ${retrait.roleAvant} »` : "Drapeau super-administrateur"} retiré : fiche ${retrait.motif === "suppression" ? "supprimée" : "désactivée"}.`,
    adminName: acteur || "Admin",
  };
}
